import { MenuItem, Select } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { withStyles } from "@mui/styles";
import classNames from "classnames";
import React, { Component, useEffect } from "react";
import { Subscription } from "rxjs";
import styled from "styled-components";

import { WorkspacePermissionSubjectType, WorkspaceResourceType } from "common/interfaces";
import CommentsController from "js/controllers/CommentsController";
import PresentationPermissionsController from "js/controllers/PresentationPermissionsController";
import WorkspaceController, { getUserPermissionsToResource } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Key } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { app } from "js/namespaces";
import { FlexBox } from "js/react/components/LayoutGrid";
import { dialogTheme } from "js/react/materialThemeOverrides";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";

import Comment from "./Comment";
import { CommentFilters } from "../../../../common/constants";

const logger = getLogger(LogGroup.COMMENTS);

const styleSelect = theme => ({
    icon: {
        color: "#A9A9A9",
    },
    select: {
        paddingLeft: 0,
        color: "#666",
        display: "flex",
        alignItems: "center",
        backgroundColor: "transparent",
        "&:selected": {
            backgroundColor: "transparent",
        },
        "&:focus": {
            backgroundColor: "transparent",
        },
    },
    nativeInput: {
        width: "auto",
    },
});

const StyledMenuItem = styled(MenuItem)`
    &&& {
        color: #666;
        font-size: 12px;

        &.Mui-selected {
            color: #666;
        }        
    }
`;

const CommentFilterSelect = styled(withStyles(styleSelect)(Select))`
    &&& {
        margin-top: 8px;
    }
`;

const CommentsList = styled.div`
    overflow-y: auto;
    height: 100%;
    margin-top: 10px;
`;

const DarkSelect = styled.span`
    font-size: 12px;
    color: #666;
`;

class CommentsPane extends Component {
    state = {
        commentFilter: CommentFilters.CurrentSlide,
        canEditPresentation: false,
        assignedUser: null,
        isPresentationOwner: false,
    }

    constructor() {
        super();

        this.user = app.user.getAuthUser();
        this.commentListRef = React.createRef();
        this.subs = new Subscription();
        this.handleGlobalKeyDown = null;
    }

    get teammates() {
        const { byViewer } = this.props;
        if (byViewer) {
            return [];
        }

        // Explicitly get from WorkspaceController as users prop overlap with PresentationPermissionsController
        const users = WorkspaceController.users;

        return users
            .map(user => ({
                uid: user.uid,
                displayName: user.displayName,
                email: user.email,
                photoURL: user.photoURL
            }));
    }

    get collaborators() {
        const { byViewer } = this.props;
        if (byViewer) {
            return [];
        }

        const { permissions, invites } = this.props;

        const collaborators = permissions
            .filter(p => p.subjectType === WorkspacePermissionSubjectType.USER)
            .map(p => WorkspaceController.users.some(u => u.uid === p.subjectId) ? { ...WorkspaceController.users.find(u => u.uid === p.subjectId), permission: p } : null)
            .filter(Boolean)
            .map(user => ({
                uid: user.uid,
                displayName: user.displayName,
                email: user.email,
                photoURL: user.photoURL,
                permissionType: user.permission.write.object ? "edit" : "view",
                isPending: false
            }));

        permissions.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER_GROUP)
            .forEach(p => {
                const group = WorkspaceController.userGroups.find(g => g.id === p.subjectId);
                if (!group) {
                    return;
                }
                const groupUsers = WorkspaceController.users.filter(user => user.groupIds.includes(group.id)).filter(user => !collaborators.some(c => c.uid === user.uid));
                groupUsers.forEach(user => {
                    collaborators.push({
                        uid: user.uid,
                        displayName: user.displayName,
                        email: user.email,
                        photoURL: user.photoURL,
                        permissionType: p.write.object ? "edit" : "view",
                        isPending: false
                    });
                });
            });

        const invitedCollaborators = invites
            .map(i => ({
                uid: i.inviteeUid,
                displayName: i.inviteeEmail,
                email: i.inviteeEmail,
                photoURL: null,
                permissionType: i.permissions.write ? "edit" : "view",
                isPending: true
            }));

        return [...collaborators, ...invitedCollaborators];
    }

    async componentDidMount() {
        const {
            assignedToSlideUser$,
        } = ds.getObservables();

        this.subs.add(
            assignedToSlideUser$.subscribe(assignedUser => this.setState({ assignedUser }))
        );

        const presentation = ds.selection.presentation;
        const isPresentationOwner = presentation?.get("userId") === this.user.uid;
        let canEditPresentation = isPresentationOwner;
        if (!canEditPresentation) {
            const permissions = await getUserPermissionsToResource({
                workspaceId: AppController.workspaceId,
                resourceId: presentation.id,
                resourceType: presentation.get("isTemplate") ? WorkspaceResourceType.PRESENTATION_TEMPLATE : WorkspaceResourceType.PRESENTATION
            });
            canEditPresentation = permissions.write.object;
        }

        this.setState({
            canEditPresentation,
            isPresentationOwner
        });

        await PresentationEditorController.setHandleKeyEvents(false);

        this.handleGlobalKeyDown = evt => {
            if (evt.which === Key.ESCAPE) {
                evt.preventDefault();
                evt.stopPropagation();
                this.props.close && this.props.close();
            }

            if (evt.metaKey || evt.ctrlKey) {
                switch (evt.which) {
                    case Key.KEY_C:
                        document.execCommand("copy");
                        break;
                    case Key.KEY_X:
                        document.execCommand("cut");
                        break;
                    case Key.KEY_F:
                        if (PresentationEditorController.isInitialized) {
                            PresentationEditorController.handleShowSearchForText(true);
                        }
                        evt.stopPropagation();
                        evt.preventDefault();
                        break;
                }
            }
        };
        window.addEventListener("keydown", this.handleGlobalKeyDown);

        trackActivity("Comments", "Opened");
    }

    componentWillUnmount() {
        this.subs.unsubscribe();
        window.removeEventListener("keydown", this.handleGlobalKeyDown);

        PresentationEditorController.setHandleKeyEvents(true);

        trackActivity("Comments", "Closed");
    }

    postComment = async (text, taggedUsers, pendingTaggedUsers) => {
        try {
            const {
                presentation,
                byViewer
            } = this.props;

            const { currentSlide } = this.props;
            const slideId = currentSlide.id;
            const presentationId = presentation.id;

            // Creating a proper signature for the comment to be posted
            const comment = {
                text,
                taggedUsers,
                pendingTaggedUsers,
                byViewer,
            };

            const { id: commentId } = await CommentsController.createComment(slideId, comment);

            const props = {
                ...presentation.getAnalytics(),
                slide_id: slideId,
                comment_id: commentId,
                byViewer,
            };
            trackActivity("Comment", "Created", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] postComment() failed");
        }
    }

    removeComment = async comment => {
        try {
            const {
                presentation,
                byViewer
            } = this.props;

            const commentId = comment.id;
            const slideId = comment.slideId;

            await CommentsController.deleteComment(commentId, slideId);

            const props = {
                ...presentation.getAnalytics(),
                slide_id: slideId,
                comment_id: commentId,
                byViewer,
            };
            trackActivity("Comment", "Deleted", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] removeComment() failed");
        }
    }

    updateComment = async (comment, text, taggedUsers, pendingTaggedUsers) => {
        try {
            const {
                presentation,
                byViewer
            } = this.props;

            const commentId = comment.id;
            const slideId = comment.slideId;
            const presentationId = presentation.id;

            // Will be updating only text and tagged users
            const commentUpdates = {
                text,
                taggedUsers,
                pendingTaggedUsers
            };
            await CommentsController.updateComment(commentId, slideId, commentUpdates);

            const props = {
                ...presentation.getAnalytics(),
                slide_id: slideId,
                comment_id: commentId,
                byViewer,
            };
            trackActivity("Comment", "Edited", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[comments] updateComment() failed");
        }
    }

    markCommentAsRead = async comment => {
        try {
            const commentId = comment.id;
            const slideId = comment.slideId;

            await CommentsController.markCommentAsRead(commentId, slideId);

            await AppController.notificationsService?.markAsReadByCommentId(commentId);
        } catch (err) {
            logger.error(err, "[comments] markCommentAsRead() failed");
        }
    }

    render() {
        const {
            commentFilter,
            canEditPresentation,
            assignedUser,
            isPresentationOwner
        } = this.state;

        const {
            comments: rawComments,
            currentSlide,
            goToSlide,
            transitionState,
            presentation,
            byViewer
        } = this.props;

        const slideIndex = presentation.getSlideIndex(currentSlide.id);

        const slideComments = [...rawComments].sort((a, b) => b.createdAt - a.createdAt).filter(c => c.slideId === currentSlide.id).filter(c => (c.byViewer ?? false) === byViewer);
        const allComments = [...rawComments].sort((a, b) => b.createdAt - a.createdAt).filter(c => (c.byViewer ?? false) === byViewer);

        return (
            <MuiThemeProvider theme={dialogTheme}>
                <FlexBox left middle>
                    <CommentFilterSelect
                        value={commentFilter}
                        onChange={event => this.setState({ commentFilter: event.target.value })}
                        disableUnderline
                    >
                        <StyledMenuItem value={CommentFilters.CurrentSlide}>
                            <DarkSelect>This Slide</DarkSelect>
                            &nbsp;
                            <span>({slideComments.length})</span>
                        </StyledMenuItem>
                        <StyledMenuItem value={CommentFilters.AllSlides}>
                            <DarkSelect>All Slides</DarkSelect>
                            &nbsp;
                            <span>({allComments.length})</span>
                        </StyledMenuItem>
                    </CommentFilterSelect>
                </FlexBox>
                <CommentsList
                    ref={this.commentListRef}
                    className={classNames({
                        [`comment-list`]: true,
                        [`viewer`]: byViewer,
                        [`editor`]: !byViewer,
                    })}
                >
                    <Comment
                        isNew
                        key="newComment"
                        post={(text, taggedUsers, pendingTaggedUsers) => {
                            this.postComment(text, taggedUsers, pendingTaggedUsers);
                        }}
                        author={{ ...this.user, displayName: this.user.displayName || this.user.email }}
                        cancelAddingComment={() => this.setState({ isAddingComment: false })}
                        collaborators={this.collaborators}
                        teammates={this.teammates}
                        slideIndex={slideIndex}
                        byViewer={byViewer}
                        assignedUser={assignedUser}
                        canEdit={transitionState === "entered"}
                        currentSlide={currentSlide}
                        presentation={presentation}
                    />
                    {
                        (commentFilter === CommentFilters.AllSlides ? allComments : slideComments).map(comment => (
                            <Comment
                                key={comment.id}
                                isMy={comment.authorUid === this.user.uid}
                                byViewer={comment.byViewer}
                                isPresentationOwner={isPresentationOwner}
                                canEditPresentation={canEditPresentation}
                                remove={() => this.removeComment(comment)}
                                update={(text, taggedUsers, pendingTaggedUsers) => this.updateComment(comment, text, taggedUsers, pendingTaggedUsers)}
                                markAsRead={() => this.markCommentAsRead(comment)}
                                text={comment.text}
                                slideIndex={presentation.getSlideIndex(comment.slideId)}
                                author={comment.author}
                                isRead={comment.readBy && comment.readBy[this.user.uid]}
                                createdAt={comment.createdAt}
                                assignedUser={assignedUser}
                                collaborators={this.collaborators}
                                teammates={this.teammates}
                                goToSlide={slideIndex => goToSlide(slideIndex)}
                                canEdit={transitionState === "entered"}
                                currentSlide={currentSlide}
                                presentation={presentation}
                            />
                        ))
                    }
                </CommentsList>
            </MuiThemeProvider>
        );
    }
}

const CommentsPaneWithPermissionsInitializer = PresentationPermissionsController.withStateInitializer(CommentsController.withInitializedState(CommentsPane), ({ presentation }) => presentation?.id);
const CommentsPaneWithPermissionsState = CommentsController.withInitializedState(PresentationPermissionsController.withState(CommentsPane));

function CommentsPaneWrapper(props) {
    const { byViewer, presentation, initializeController, firebaseUser } = props;

    useEffect(() => {
        if (!initializeController) {
            return;
        }

        CommentsController.initialize({
            presentationId: presentation.id,
            presentationLinkId: presentation.get("link")?.id,
            byViewer,
            firebaseUser
        });

        return () => {
            CommentsController.reset();
        };
    }, [presentation?.id, initializeController]);

    if (byViewer) {
        // The comments pane is rendered in the player, so we don't need the data
        // from the presentation permissions controller, keep it not initialized
        return <CommentsPaneWithPermissionsState {...props} />;
    }

    // Ensure the presentation permissions controller is initialized if comments are
    // viewed from the editor
    return <CommentsPaneWithPermissionsInitializer {...props} />;
}

export default withFirebaseUser(CommentsPaneWrapper);
